/**
 * @element qds-field
 *
 * Layout and text container for a single form control. Owns the vertical
 * rhythm of label / control / hint / error and nothing else. It is
 * convenience, not correctness -- every control is fully accessible
 * without it.
 *
 * @attr {string} label - Label text displayed above (or beside, in horizontal) the control. Pushed to a slotted consumer.
 * @attr {string} hint - Helper text displayed below the control. Pushed to a slotted consumer.
 * @attr {string} error-text - Error message; shown when `invalid` is true. Pushed to a slotted consumer. Presence does not itself mark invalid.
 * @attr {boolean} required - Renders the visual required marker and pushes to a slotted consumer.
 * @attr {boolean} invalid - Shows `error-text` and pushes to a slotted consumer.
 * @attr {boolean} disabled - Dims label and hint, pushes to a slotted consumer.
 * @attr {'vertical'|'horizontal'} orientation - Layout direction. `horizontal` puts the label in a left column of `--qds-field-label-width`. Default `vertical`.
 * @attr {boolean} reserve-message - Reserves one line below the control so validation does not shift layout. Default `false`.
 * @attr {'sm'|'md'|'lg'} size - Sets label type size and pushes to a slotted consumer. Default `md`.
 *
 * @csspart container - The outer wrapper.
 * @csspart label - The label text element.
 * @csspart required - The required marker span.
 * @csspart control - The slot wrapper for the slotted control.
 * @csspart hint - The helper text element.
 * @csspart error - The error message element.
 *
 * @example
 * <qds-field label="Email" hint="We will never share your email" required>
 *   <qds-text-input></qds-text-input>
 * </qds-field>
 */

import { loadBaseCSS } from '../shared/button-utils.js';

export class QdsField extends HTMLElement {
  private _label: string = '';
  private _hint: string = '';
  private _errorText: string = '';
  private _required: boolean = false;
  private _invalid: boolean = false;
  private _disabled: boolean = false;
  private _orientation: string = 'vertical';
  private _reserveMessage: boolean = false;
  private _size: string = 'md';

  /** True when the author explicitly set error-text (not mirrored from a validity event). */
  private _hasExplicitErrorText: boolean = false;

  /** Guard so internal attribute writes are not treated as author-set. */
  private _internalUpdate: boolean = false;

  /** Warn once per connection when a non-consumer is slotted. */
  private _nonConsumerWarned: boolean = false;

  private _slotChangeHandler: (() => void) | null = null;
  private _validityChangeHandler: ((e: Event) => void) | null = null;

  static get observedAttributes(): string[] {
    return [
      'label',
      'hint',
      'error-text',
      'required',
      'invalid',
      'disabled',
      'orientation',
      'reserve-message',
      'size',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._syncPrivateFields();
    this._hasExplicitErrorText =
      this.hasAttribute('error-text') && this._errorText !== '';
    this._nonConsumerWarned = false;

    if (!this._slotChangeHandler) {
      this._slotChangeHandler = (): void => this._pushToConsumer();
    }
    if (!this._validityChangeHandler) {
      this._validityChangeHandler = (e: Event): void =>
        this._handleValidityChange(e);
    }

    this.addEventListener('qds-validity-change', this._validityChangeHandler);

    this.render();
    this._pushToConsumer();
  }

  disconnectedCallback(): void {
    if (this._validityChangeHandler) {
      this.removeEventListener('qds-validity-change', this._validityChangeHandler);
    }
    if (this._slotChangeHandler) {
      const slot = this.shadowRoot?.querySelector('slot');
      if (slot) {
        slot.removeEventListener('slotchange', this._slotChangeHandler);
      }
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    if (name === 'error-text' && !this._internalUpdate) {
      this._hasExplicitErrorText = newValue !== null && newValue !== '';
    }

    this._syncPrivateFields();

    if (this.isConnected) {
      this.render();
      this._pushToConsumer();
    }
  }

  // ── Private helpers ────────────────────────────────────────────────

  private _syncPrivateFields(): void {
    this._label = this.getAttribute('label') || '';
    this._hint = this.getAttribute('hint') || '';
    this._errorText = this.getAttribute('error-text') || '';
    this._required = this.hasAttribute('required');
    this._invalid = this.hasAttribute('invalid');
    this._disabled = this.hasAttribute('disabled');
    this._orientation = this.getAttribute('orientation') || 'vertical';
    this._reserveMessage = this.hasAttribute('reserve-message');
    this._size = this.getAttribute('size') || 'md';
  }

  private _handleValidityChange(e: Event): void {
    const detail = (e as CustomEvent).detail as
      | { valid: boolean; message: string }
      | undefined;
    if (!detail || typeof detail.valid === 'undefined') return;

    // Only mirror when the author has not set an explicit error-text.
    if (this._hasExplicitErrorText) return;

    this._internalUpdate = true;

    if (detail.valid) {
      this.removeAttribute('invalid');
      this.removeAttribute('error-text');
    } else {
      this.setAttribute('invalid', '');
      if (detail.message) {
        this.setAttribute('error-text', String(detail.message));
      }
    }

    this._internalUpdate = false;
    this._syncPrivateFields();
    this.render();
    this._pushToConsumer();
  }

  /**
   * Returns the slotted elements assigned to the field's slot, with a
   * fallback to `this.children` for environments where `assignedElements`
   * is unavailable or not yet populated (e.g. jsdom).
   */
  private _getAssignedElements(): Element[] {
    if (!this.shadowRoot) return [];
    const slot = this.shadowRoot.querySelector('slot') as HTMLSlotElement | null;
    if (!slot) return [];

    let assigned: Element[];
    if (typeof slot.assignedElements === 'function') {
      assigned = slot.assignedElements({ flatten: true });
    } else {
      assigned = [];
    }

    if (assigned.length === 0 && this.children.length > 0) {
      assigned = Array.from(this.children);
    }

    return assigned;
  }

  private _pushToConsumer(): void {
    const assigned = this._getAssignedElements();
    if (assigned.length === 0) return;

    let consumer: Element | null = null;
    let hasNonConsumer = false;

    for (const el of assigned) {
      const tagName = el.tagName.toLowerCase();
      // A custom-element tag (contains a hyphen) that hasn't upgraded yet
      // has no static qdsFieldConsumer on its constructor regardless of
      // what the real control declares — its module may simply still be
      // loading. Deciding "not a consumer" here is permanent: nothing else
      // re-checks once the real definition arrives, since upgrade doesn't
      // fire slotchange. Defer the decision instead of guessing wrong.
      // (Checks the registry directly rather than `:defined` — jsdom 26
      // does not implement that pseudo-class and throws on `.matches()`.)
      const registered = tagName.includes('-') ? customElements.get(tagName) : undefined;
      if (tagName.includes('-') && (!registered || el.constructor !== registered)) {
        customElements.whenDefined(tagName).then(() => {
          if (this.isConnected) this._pushToConsumer();
        });
        continue;
      }

      const ctor = el.constructor as unknown as {
        qdsFieldConsumer?: boolean;
      };
      if (ctor.qdsFieldConsumer === true) {
        consumer = el;
        break;
      }
      hasNonConsumer = true;
    }

    if (consumer) {
      // Push ONLY attributes the field actually has.
      // Never clear an attribute the field does not define.
      if (this.hasAttribute('label')) {
        consumer.setAttribute('label', this._label);
      }
      if (this.hasAttribute('hint')) {
        consumer.setAttribute('hint', this._hint);
      }
      if (this.hasAttribute('error-text')) {
        consumer.setAttribute('error-text', this._errorText);
      }
      if (this.hasAttribute('required')) {
        consumer.setAttribute('required', '');
      }
      if (this.hasAttribute('invalid')) {
        consumer.setAttribute('invalid', '');
      }
      if (this.hasAttribute('disabled')) {
        consumer.setAttribute('disabled', '');
      }
      if (this.hasAttribute('size')) {
        consumer.setAttribute('size', this._size);
      }
    } else if (hasNonConsumer && !this._nonConsumerWarned) {
      console.debug(
        'qds-field: slotted element is not a qdsFieldConsumer. ' +
          'Label is rendered visually; ARIA is left to the author.',
      );
      this._nonConsumerWarned = true;
    }
  }

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Render ─────────────────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;

    const showLabel = this._label !== '';
    const showRequired = this._required;
    const showError = this._invalid && this._errorText !== '';
    const showHint = this._hint !== '' && !showError;
    const showReservedRow = this._reserveMessage;
    const showMessageRow = showHint || showError || showReservedRow;

    const sizeFont =
      this._size === 'sm' ? '14px' : this._size === 'lg' ? '18px' : '16px';
    const isHorizontal = this._orientation === 'horizontal';

    const containerClasses = [
      'container',
      isHorizontal ? 'horizontal' : '',
      this._disabled ? 'disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const messageRowClasses = [
      'message-row',
      showReservedRow ? 'reserved' : '',
    ]
      .filter(Boolean)
      .join(' ');

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        .container {
          display: flex;
          flex-direction: column;
          gap: var(--qds-field-gap, var(--theme-space-1, 4px));
        }

        .container.horizontal {
          flex-direction: row;
          align-items: flex-start;
        }

        .label {
          color: var(--qds-field-label-color, var(--theme-text-secondary));
          font-size: ${sizeFont};
          font-weight: 600;
          line-height: 1.4;
        }

        .container.horizontal .label {
          width: var(--qds-field-label-width, 180px);
          flex-shrink: 0;
          box-sizing: border-box;
          padding-right: var(--theme-space-2, 8px);
        }

        .required {
          color: var(--qds-field-error-color, var(--theme-error));
          margin-left: 2px;
        }

        .control-column {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: var(--qds-field-gap, var(--theme-space-1, 4px));
        }

        .control {
          width: 100%;
        }

        .message-row {
          min-height: 0;
        }

        .message-row.reserved {
          min-height: 1.4em;
        }

        .hint {
          color: var(--qds-field-hint-color, var(--theme-text-tertiary));
          font-size: 14px;
          line-height: 1.4;
        }

        .error {
          color: var(--qds-field-error-color, var(--theme-error));
          font-size: 14px;
          line-height: 1.4;
        }

        .container.disabled .label,
        .container.disabled .hint {
          opacity: 0.6;
        }
      </style>

      <div part="container" class="${containerClasses}">
        ${
          showLabel
            ? `<div part="label" class="label">${this._escapeHtml(this._label)}${
                showRequired
                  ? ' <span part="required" class="required" aria-hidden="true">*</span>'
                  : ''
              }</div>`
            : ''
        }
        <div class="control-column">
          <div part="control" class="control">
            <slot></slot>
          </div>
          ${
            showMessageRow
              ? `<div class="${messageRowClasses}">${
                  showError
                    ? `<div part="error" class="error">${this._escapeHtml(this._errorText)}</div>`
                    : ''
                }${
                  showHint
                    ? `<div part="hint" class="hint">${this._escapeHtml(this._hint)}</div>`
                    : ''
                }</div>`
              : ''
          }
        </div>
      </div>
    `;

    // Re-attach slotchange listener to the freshly created slot.
    if (this._slotChangeHandler) {
      const slot = this.shadowRoot.querySelector('slot');
      if (slot) {
        slot.addEventListener('slotchange', this._slotChangeHandler);
      }
    }
  }
}

if (!customElements.get('qds-field')) {
  customElements.define('qds-field', QdsField);
}
